import moment from 'moment';
import find from 'lodash/find';
import { IjobHour, Ioption, Iuser } from '../../models';

export interface IjobHourPrefill {
    quantity: number;
    date: moment.Moment | null;
    description: Ioption | null; // labor rate option
    engineerVanID: Ioption | null; // FSE option
}

const buildEngineerOption = (user: Iuser): Ioption => {
    const fullName = user.first && user.last ? `${user.first} ${user.last}` : 'unknown';
    const label = user.engineerVanID ? `${fullName}: ${user.engineerVanID}` : `${fullName} (No ID)`;
    return { value: user.id, label };
};

/**
 * Build the form prefill values for the job-hours form. Works for both add-mode
 * (empty jobHour) and edit-mode (populated jobHour):
 *  - date: parse the stored SQL/ISO string into a moment for the datetime picker
 *  - description: the labor-rate option matching the row's laborRateID
 *  - engineerVanID: the FSE option for the row's engineer (mapped from the van id
 *    via fseUsers), falling back to the current user when there is no match
 */
export const buildJobHourPrefill = (
    jobHour: IjobHour,
    laborRatesOptions: Ioption[],
    fseOptions: Ioption[],
    fseUsers: Iuser[],
    currentUserID: string
): IjobHourPrefill => {
    let date: moment.Moment | null = null;
    if (typeof jobHour.date === 'string' && jobHour.date.trim().length > 0) {
        date = moment.utc(jobHour.date).local(true);
    }

    const description = find(laborRatesOptions, { value: jobHour.laborRateID }) || null;

    let engineerVanID: Ioption | null = null;
    if (jobHour.engineerVanID) {
        const matchedUser = fseUsers.find(user => user.engineerVanID === jobHour.engineerVanID);
        if (matchedUser) {
            // Prefer the active FSE option; if the original engineer is inactive/deleted
            // (absent from the active options), preserve them so editing does not silently
            // reassign the hour to the current user.
            engineerVanID = find(fseOptions, { value: matchedUser.id }) || buildEngineerOption(matchedUser);
        }
    }
    if (!engineerVanID) {
        engineerVanID = find(fseOptions, { value: currentUserID }) || null;
    }

    return {
        quantity: jobHour.quantity,
        date,
        description,
        engineerVanID
    };
};
